import { createContext, useContext } from 'react';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import { DEFAULT_TIME_COMMITMENT } from 'bundles/adaptive-learning-common/constants';
import type { AdaptiveLearningPlan, LearningBlock } from 'bundles/adaptive-learning-common/types';

export type RefetchPlanOptions = {
  showLoadingDialog?: boolean;
};

type CourseMaterials = $TSFixMe; // CourseMaterials based on pages/open-course/common/models/courseMaterials

export type RefetchPlanResult = {
  plan: AdaptiveLearningPlan;
  materials: InstanceType<CourseMaterials>;
};

export type RegeneratePlanOptions = {
  sessionDurationMinutes: number;
  /**
   * Explicit sub-skill names to pack into the active block, in queue order (a contiguous prefix of
   * the learner's eligible sub-skills). Overrides duration-based packing for the active block only;
   * later blocks still pack from `sessionDurationMinutes`. Omit or leave empty for duration-only.
   */
  targetSubSkillNames?: string[];
};

/**
 * A pending "apply changes and start learning": the plan state the apply was made against, so the
 * one-pager can tell when the regenerated plan has landed and forward into content.
 *
 * This lives on the context rather than in the component because regenerating navigates to the new
 * plan's one-pager and remounts the card, while the provider survives. Holding it here also lets the
 * provider keep its loading overlay up across the whole hop, so the learner never sees the
 * intermediate one-pager flash by.
 */
export type AutoStartRequest = {
  fromBlockId: string;
  fromMinutes: number;
  fromItemCount: number;
};

export type AdaptiveLearningContextType = {
  isEnabled: boolean;
  show: boolean;
  loading: boolean;
  setShow: (show: boolean) => void;
  adaptivePlan?: AdaptiveLearningPlan;
  timeCommitment: number;
  planUpdated: boolean;
  getLearningBlock: (itemId: string) => LearningBlock | undefined;
  regeneratePlan: (options: RegeneratePlanOptions) => Promise<void>;
  refetchPlan: (options?: RefetchPlanOptions) => Promise<RefetchPlanResult | undefined>;
  isProgressDialogOpen: boolean;
  setIsProgressDialogOpen: (open: boolean) => void;
  isNonPersonalizedLoadingDialogOpen: boolean;
  setIsNonPersonalizedLoadingDialogOpen: (open: boolean) => void;
  autoStart?: AutoStartRequest;
  setAutoStart: (request: AutoStartRequest | undefined) => void;
  /**
   * Raised by a surface that has started work the plan-loading dialog should already be covering,
   * before the regeneration that would raise it on its own. Submitting proficiency is the case: it
   * round-trips first, and `planLoading` deliberately ignores refetches, so without this the learner
   * waits on an idle-looking card. Released by the caller once the regeneration is under way, and by
   * a timeout if it never gets there.
   */
  holdPlanLoading: boolean;
  setHoldPlanLoading: (hold: boolean) => void;
  isBlockCelebrationDialogOpen: boolean;
  setIsBlockCelebrationDialogOpen: (open: boolean) => void;
  getNextItem: () => Promise<ItemMetadata | undefined>;
};

export const AdaptiveLearningContext = createContext<AdaptiveLearningContextType>({
  isEnabled: false,
  show: false,
  loading: false,
  planUpdated: false,
  setShow: () => undefined,
  timeCommitment: DEFAULT_TIME_COMMITMENT,
  getLearningBlock: () => undefined,
  regeneratePlan: () => Promise.resolve(),
  isProgressDialogOpen: false,
  setIsProgressDialogOpen: () => undefined,
  refetchPlan: () => Promise.resolve(undefined),
  isNonPersonalizedLoadingDialogOpen: false,
  setIsNonPersonalizedLoadingDialogOpen: () => undefined,
  setAutoStart: () => undefined,
  holdPlanLoading: false,
  setHoldPlanLoading: () => undefined,
  isBlockCelebrationDialogOpen: false,
  setIsBlockCelebrationDialogOpen: () => undefined,
  getNextItem: () => Promise.resolve(undefined),
});

export const useAdaptiveLearningContext = () => useContext(AdaptiveLearningContext);
